import { IconButton } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import {
  SelectionCell,
  SelectionHeaderCell,
} from "@src/components/ui-kit/DataTable/Body/components";
import {
  resolveColumnId,
  resolveColumnLabel,
} from "@src/components/ui-kit/DataTable/Body/helpers";
import type { DataTableBodyStore } from "@src/components/ui-kit/DataTable/Body/store";
import type {
  BodyColumnDef,
  BodyProps,
  BodyRow,
} from "@src/components/ui-kit/DataTable/types";
import { CollapseIcon, Icon } from "@src/components/ui-kit/Icon";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo } from "react";

export const SELECTION_COLUMN_ID = "__tanstack_selection";
const SELECTION_COLUMN_SIZE = 44;
export const EXPANDER_COLUMN_ID = "__tanstack_expander";
export const EXPANDER_COLUMN_SIZE = 60;
export const ACTIONS_COLUMN_ID = "actions";
export const SYSTEM_COLUMN_IDS = [SELECTION_COLUMN_ID, EXPANDER_COLUMN_ID];
const ACTION_COLUMN_SIZE = 70;

interface UseTableBodyColumnArgs<Row extends BodyRow>
  extends Pick<
    BodyProps<Row, unknown>,
    "onExpandAllRows" | "onExpandRow" | "rowSelectionMode" | "columns"
  > {
  pageSelectableIds: string[];
  tanstackBodyStore: DataTableBodyStore;
  isRowSelectionEnabled: boolean;
  hasSubRows: boolean;
  hideExpandColumn: boolean;
}
export function useTableBodyColumns<
  Row extends BodyRow,
  OrderByColumn = unknown,
>({
  pageSelectableIds,
  tanstackBodyStore,
  rowSelectionMode,
  hasSubRows,
  hideExpandColumn,
  onExpandAllRows,
  onExpandRow,
  columns,
  isRowSelectionEnabled,
}: UseTableBodyColumnArgs<Row>) {
  const selectionColumn = useMemo<ColumnDef<Row, unknown>>(
    () => ({
      id: SELECTION_COLUMN_ID,
      size: SELECTION_COLUMN_SIZE,
      minSize: SELECTION_COLUMN_SIZE,
      maxSize: SELECTION_COLUMN_SIZE,
      enableColumnOrdering: false,
      enableHiding: false,
      enableResizing: false,
      header: ({ table }) => (
        <SelectionHeaderCell
          table={table}
          pageSelectableIds={pageSelectableIds}
          rowSelectionMode={rowSelectionMode}
          selectedIds={tanstackBodyStore.bulkActionsStore.selectedIds}
        />
      ),
      cell: ({ row, table }) => (
        <SelectionCell
          row={row}
          table={table}
          rowSelectionMode={rowSelectionMode}
          selectedIds={tanstackBodyStore.bulkActionsStore.selectedIds}
        />
      ),
    }),
    [
      pageSelectableIds,
      rowSelectionMode,
      tanstackBodyStore.bulkActionsStore.selectedIds,
    ],
  );

  const expanderColumn = useMemo<BodyColumnDef<Row, OrderByColumn>>(
    () => ({
      id: EXPANDER_COLUMN_ID,
      size: EXPANDER_COLUMN_SIZE,
      minSize: EXPANDER_COLUMN_SIZE,
      maxSize: EXPANDER_COLUMN_SIZE,
      enableColumnOrdering: false,
      enableHiding: false,
      enableResizing: false,
      header: ({ table }) => {
        const handleClick = () => {
          const shouldExpandAll = !table.getIsAllRowsExpanded();

          if (shouldExpandAll) {
            onExpandAllRows?.();
          }

          table.toggleAllRowsExpanded(shouldExpandAll);
        };

        return (
          <IconButton
            aria-label={t`Toggle all rows`}
            icon={<CollapseIcon />}
            onClick={handleClick}
            size="sm"
            variant="ghost"
          />
        );
      },
      cell: ({ row }) => {
        if (!row.getCanExpand()) {
          return null;
        }

        const iconName = row.getIsExpanded() ? "chevron-up" : "chevron-down";

        const handleClick = () => {
          const shouldExpand = !row.getIsExpanded();

          if (shouldExpand) {
            onExpandRow?.(row.original);
          }

          row.toggleExpanded(shouldExpand);
        };

        return (
          <IconButton
            aria-label={t`Toggle row`}
            onClick={handleClick}
            size="sm"
            variant="ghost"
          >
            <Icon boxSize="4" name={iconName} />
          </IconButton>
        );
      },
    }),
    [onExpandAllRows, onExpandRow],
  );

  const normalizedDataColumns = useMemo<ColumnDef<Row, unknown>[]>(
    () =>
      columns.map((column, index) => {
        const id = resolveColumnId(column, index);

        return {
          ...column,
          id,
          size:
            id === ACTIONS_COLUMN_ID
              ? (column.size ?? ACTION_COLUMN_SIZE)
              : column.size,
          enableResizing:
            id === ACTIONS_COLUMN_ID ? false : column.enableResizing,
          enableHiding: id === ACTIONS_COLUMN_ID ? false : column.enableHiding,
        };
      }),
    [columns],
  );

  const dataColumnIds = useMemo(
    () => normalizedDataColumns.map((column) => String(column.id)),
    [normalizedDataColumns],
  );

  const dataColumnIdsSet = useMemo(
    () => new Set(dataColumnIds),
    [dataColumnIds],
  );

  const hideableDataColumnIds = useMemo(
    () =>
      normalizedDataColumns
        .filter((column) => column.enableHiding !== false)
        .map((column) => String(column.id)),
    [normalizedDataColumns],
  );

  const columnLabelById = useMemo(
    () =>
      normalizedDataColumns.reduce((acc, column) => {
        acc.set(String(column.id), resolveColumnLabel(column));
        return acc;
      }, new Map<string, string>()),
    [normalizedDataColumns],
  );

  useEffect(() => {
    tanstackBodyStore.syncDataColumnOrder(dataColumnIds);
    tanstackBodyStore.syncHiddenColumnIds(hideableDataColumnIds);
  }, [
    dataColumnIds,
    hideableDataColumnIds,
    tanstackBodyStore.syncDataColumnOrder,
    tanstackBodyStore.syncHiddenColumnIds,
  ]);

  const tableColumns = useMemo<BodyColumnDef<Row, OrderByColumn>[]>(() => {
    const cols: ColumnDef<Row, unknown>[] = [];

    if (isRowSelectionEnabled) {
      cols.push(selectionColumn);
    }

    if (hasSubRows && !hideExpandColumn) {
      cols.push(expanderColumn);
    }

    cols.push(...normalizedDataColumns);

    return cols;
  }, [
    isRowSelectionEnabled,
    hasSubRows,
    hideExpandColumn,
    expanderColumn,
    normalizedDataColumns,
    selectionColumn,
  ]);

  return {
    tableColumns,
    columnLabelById,
    hideableDataColumnIds,
    dataColumnIds,
    dataColumnIdsSet,
  };
}
